import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Controller, FormProvider, useFormContext } from 'react-hook-form';

import { cn } from '../../lib/utils';
import { Label } from './label';
import { Input } from './input';
import DatePicker from 'react-datepicker';
import InputMask from 'react-input-mask';
import CustomSelect from '../CustomSelect';
import CustomMultiSeclect from '../CustomMultiSelect';
import CustomMultiSelect from '../CustomMultiSelect';

const Form = FormProvider;

const FormFieldContext = React.createContext({});

const FormField = ({ ...props }) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState, formState, control } = useFormContext();

  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error('useFormField should be used within <FormField>');
  }
  const { isSubmitted } = formState;

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    isSubmitted,
    ...fieldState,
  };
};

const FormItemContext = React.createContext({});

const FormItem = React.forwardRef(({ className, ...props }, ref) => {
  const id = React.useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn('', className)} {...props} />
    </FormItemContext.Provider>
  );
});
FormItem.displayName = 'FormItem';

const FormLabel = React.forwardRef(({ className, ...props }, ref) => {
  const { error, formItemId } = useFormField();

  return (
    <Label
      ref={ref}
      className={cn(error && 'text-red-500 dark:text-red-900', className)}
      htmlFor={formItemId}
      {...props}
    />
  );
});
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef(({ ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={!error ? `${formDescriptionId}` : `${formDescriptionId} ${formMessageId}`}
      aria-invalid={!!error}
      {...props}
    />
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { error, formDescriptionId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <p ref={ref} id={formDescriptionId} className={cn(`school_info ${error && 'hidden'}`, className)} {...props} />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p ref={ref} id={formMessageId} className={cn('', className)} {...props}>
      {body}
    </p>
  );
});
FormMessage.displayName = 'FormMessage';
const FormSubOptionMessage = React.forwardRef(({ className, children, index, selectedAnswers, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  if (error) {
    const errorIndex = error.map((err, index) => typeof err === 'Object' && index);
  }
  const body = error ? String(error?.message || error?.root?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p ref={ref} id={formMessageId} className={cn('', className)} {...props}>
      {body}
    </p>
  );
});
FormSubOptionMessage.displayName = 'FormSubOptionMessage';

const FormInput = React.forwardRef(
  ({ className, children, required, field = {}, isUserRegistration, ...props }, ref) => {
    const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, name, ...rest } = useFormField();
    return (
      <Input
        ref={ref}
        id={formMessageId}
        error={error}
        isDirty={isDirty}
        isUserRegistration={isUserRegistration}
        className={cn(
          `${error ? 'border_red' : ''} ${
            isSubmitted && !error && field.value && !(typeof field.value === 'object') ? 'border_green' : ''
          }  ${isDirty && field?.value?.length > 0 ? 'border_blue' : ''} ${
            isUserRegistration ? '!border-none !bg-[#F7F7F8] !text-[#1d1d1d]' : ''
          }
        disabled:border-gray-400 disabled:text-gray-400 disabled:border-2 `,
          className
        )}
        {...props}
      />
    );
  }
);
FormMessage.displayName = 'FormInput';

const FormCheck = React.forwardRef(({ className, children, required, field = {}, ...props }, ref) => {
  const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <Input
      ref={ref}
      id={formMessageId}
      error={error}
      className={cn(
        `${error ? 'border_red' : ''} ${
          isSubmitted && !error && field.value ? 'border_green' : ''
        } disabled:border-gray-400 disabled:text-gray-400 disabled:border-2`,
        className
      )}
      {...props}
    />
  );
});
FormMessage.displayName = 'FormCheck';

const FormDatePicker = React.forwardRef(({ className, children, field = {}, ...props }, ref) => {
  const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <DatePicker
      ref={ref}
      id={formMessageId}
      className={cn(
        `${error ? 'border_red' : ''} ${
          isSubmitted && !error && field.value ? 'border_green' : ''
        } disabled:border-gray-400 disabled:text-gray-400 disabled:border-2 `,
        className
      )}
      {...props}
    />
  );
});
FormMessage.displayName = 'FormDatePicker';

const FormInputMask = React.forwardRef(({ className, children, required, field = {}, ...props }, ref) => {
  const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <InputMask
      ref={ref}
      id={formMessageId}
      className={cn(
        `${error ? 'border_red' : ''} ${
          isSubmitted && !error && field.value ? 'border_green' : ''
        } disabled:border-gray-400 disabled:text-gray-400 disabled:border-2 `,
        className
      )}
      {...props}
    />
  );
});
FormMessage.displayName = 'FormInputMask';

const FormSelect = React.forwardRef(({ className, children, required, field = {}, ...props }, ref) => {
  const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <CustomSelect
      ref={ref}
      id={formMessageId}
      className={cn(
        `${error ? 'border_red' : ''} ${
          isSubmitted && !error && field.value ? 'border_green' : ''
        } disabled:border-gray-400 disabled:text-gray-400 disabled:border-2 `,
        className
      )}
      {...props}
    />
  );
});
FormMessage.displayName = 'FormSelect';
const FormMultipleSelect = React.forwardRef(({ className, children, required, field = {}, ...props }, ref) => {
  const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <CustomMultiSelect
      ref={ref}
      id={formMessageId}
      className={cn(
        `${error ? 'border_red' : ''} ${
          isSubmitted && !error && field.value ? 'border_green' : ''
        } disabled:border-gray-400 disabled:text-gray-400 disabled:border-2 `,
        className
      )}
      field={field}
      {...props}
    />
  );
});
FormMessage.displayName = 'FormSelect';

const FormTextarea = React.forwardRef(({ className, children, required, field = {}, ...props }, ref) => {
  const { error, formMessageId, isTouched, invalid, isDirty, isSubmitted, ...rest } = useFormField();

  return (
    <textarea
      ref={ref}
      id={formMessageId}
      className={cn(
        `${error ? 'border_red' : ''} ${
          isSubmitted && !error && field.value ? 'border_green' : ''
        } disabled:border-gray-400 disabled:text-gray-400 disabled:border-2 `,
        className
      )}
      {...props}
    />
  );
});
FormMessage.displayName = 'FormTextarea';

export {
  useFormField,
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
  FormField,
  FormInput,
  FormDatePicker,
  FormInputMask,
  FormSelect,
  FormTextarea,
  FormCheck,
  FormMultipleSelect,
  FormSubOptionMessage,
};
